import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  fetchAuthSession,
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  signOut as amplifySignOut,
  resendSignUpCode as amplifyResendCode,
  type SignUpOutput,
} from "aws-amplify/auth";

/**
 * Native (Amplify-backed) Cognito auth — no hosted-UI redirect.
 *
 * AuthProvider holds the current session in React state, hydrated from
 * Amplify's underlying token store (which itself reads from
 * localStorage). All operations are async and surface errors as
 * thrown exceptions for forms to render.
 *
 * Tenant scoping: pulled from the verified ID token's `custom:tenantId`
 * claim. Users who sign up but haven't been linked to a tenant land on
 * the /no-tenant page until the API patches that attribute (currently
 * a manual step — see project_spotmesh_auth.md).
 */

export interface TenantSession {
  tenantId: string;
  email: string;
  name: string;
  idToken: string;
}

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  profile: { email?: string; name?: string; tenantId?: string } | null;
  idToken: string | null;
}

interface AuthValue extends AuthState {
  tenant: TenantSession | null;
  needsTenantAssignment: boolean;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signUp: (input: {
    name: string;
    email: string;
    password: string;
  }) => Promise<SignUpOutput>;
  confirmSignUp: (input: { email: string; code: string }) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Force-refresh the underlying Cognito tokens. Call this right after
   * /tenants succeeds so the next-render ID token carries the new
   * `custom:tenantId` claim and RequireTenant lets the user through.
   */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

const INITIAL: AuthState = {
  isLoading: true,
  isAuthenticated: false,
  profile: null,
  idToken: null,
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(INITIAL);

  const loadSession = useCallback(async (forceRefresh: boolean) => {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      const idToken = session.tokens?.idToken;
      if (!idToken) {
        setState({ ...INITIAL, isLoading: false });
        return;
      }
      const claims = idToken.payload as Record<string, unknown>;
      setState({
        isLoading: false,
        isAuthenticated: true,
        idToken: idToken.toString(),
        profile: {
          email: typeof claims.email === "string" ? claims.email : undefined,
          name: typeof claims.name === "string" ? claims.name : undefined,
          tenantId:
            typeof claims["custom:tenantId"] === "string"
              ? (claims["custom:tenantId"] as string)
              : undefined,
        },
      });
    } catch {
      setState({ ...INITIAL, isLoading: false });
    }
  }, []);

  const refresh = useCallback(() => loadSession(false), [loadSession]);
  const forceRefresh = useCallback(() => loadSession(true), [loadSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value: AuthValue = {
    ...state,
    tenant:
      state.isAuthenticated && state.profile?.tenantId && state.idToken
        ? {
            tenantId: state.profile.tenantId,
            email: state.profile.email ?? "",
            name: state.profile.name ?? state.profile.email ?? "Vendor",
            idToken: state.idToken,
          }
        : null,
    needsTenantAssignment:
      state.isAuthenticated && !state.profile?.tenantId,

    signIn: async ({ email, password }) => {
      const result = await amplifySignIn({
        username: email,
        password,
      });
      if (!result.isSignedIn) {
        const step = result.nextStep?.signInStep ?? "UNKNOWN";
        if (step === "CONFIRM_SIGN_UP") {
          const err = new Error(
            "Please confirm your email address first. We've re-sent a verification code.",
          );
          (err as Error & { nextStep?: string; email?: string }).nextStep =
            "CONFIRM_SIGN_UP";
          (err as Error & { nextStep?: string; email?: string }).email = email;
          throw err;
        }
        throw new Error(`Sign-in requires additional step: ${step}`);
      }
      await refresh();
    },

    signUp: async ({ name, email, password }) => {
      return amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: { email, name },
        },
      });
    },

    confirmSignUp: async ({ email, code }) => {
      await amplifyConfirmSignUp({
        username: email,
        confirmationCode: code,
      });
    },

    resendCode: async (email) => {
      await amplifyResendCode({ username: email });
    },

    signOut: async () => {
      await amplifySignOut();
      await refresh();
    },

    refresh: forceRefresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
